import React, { useEffect, useMemo, useState } from "react";
import "../styles/shared-ui.css";
import "../styles/job-detail.css";
import { useAppContext } from "../context/AppContext";
import { getJobWithRelations } from "../services/jobService";
import RichTextEditor from "../components/ui/RichTextEditor";
import SideTabs from "../components/layout/SideTabs";
import SkillTable from "../components/ui/SkillTable";
import RaciMatrix from "../components/ui/RaciMatrix";
import CurrencySelector from "../components/ui/CurrencySelector";
import { toast } from "../utils/toast";
import { LEVELS } from "../components/ui/levels";
import { useLocation, useParams } from "react-router-dom";
import useCurrencyRates from "../hooks/useCurrencyRates";
import { convertCurrency, formatMoney } from "../services/currencyService";
import DimensionAllocationPanel from "../components/ui/DimensionAllocationPanel";
import {
  PAEI_DEFAULTS,
  PAEI_DIMENSIONS,
  CAPI_DEFAULTS,
  CAPI_DIMENSIONS,
} from "../components/ui/dimensionAllocations";
import { getPaeiByJobId } from "../services/paeiService";
import { getCapiByJobId } from "../services/capiService";

const extractTaskJoinRows = (job) => {
  const taskJobs =
    job?.taskJobs || job?.task_jobs || job?.jobTasks || job?.job_tasks || [];
  return Array.isArray(taskJobs) ? taskJobs : [];
};

const extractLinkedTasks = (job) =>
  extractTaskJoinRows(job)
    .map((tj) => tj?.task || tj)
    .filter(Boolean);

const extractRaciRows = (job) =>
  extractTaskJoinRows(job)
    .map((tj) => ({
      task_id: tj?.task_id ?? tj?.task?.task_id ?? tj?.task?.id,
      role: tj?.role ?? null,
      time_allocation_percentage:
        tj?.time_allocation_percentage != null
          ? Number(tj.time_allocation_percentage)
          : null,
    }))
    .filter((r) => r.task_id != null);

const JobDetailPage = () => {
  const { jobId: jobIdCtx, setJobId, setActiveSection } = useAppContext();
  const { jobId: jobIdParam } = useParams();
  const location = useLocation();
  const routeJobId = useMemo(() => {
    if (jobIdParam != null && jobIdParam !== "") {
      try {
        return decodeURIComponent(jobIdParam);
      } catch {
        return jobIdParam;
      }
    }
    const pathname = String(location?.pathname || "");
    const match = pathname.match(/^\/jobs\/([^/]+)$/i);
    if (!match?.[1]) return null;
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }, [jobIdParam, location?.pathname]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkedTasks, setLinkedTasks] = useState([]);
  const [raciRows, setRaciRows] = useState([]);
  const [paeiValues, setPaeiValues] = useState(PAEI_DEFAULTS);
  const [capiValues, setCapiValues] = useState(CAPI_DEFAULTS);
  const [displayCurrency, setDisplayCurrency] = useState("USD");
  const { rates, refreshing: refreshingRates, refresh: onRefreshRates } =
    useCurrencyRates();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        let id = routeJobId || jobIdCtx;
        if (routeJobId) {
          try {
            localStorage.setItem("activeJobId", String(routeJobId));
          } catch {}
          try {
            setJobId(routeJobId);
          } catch {}
        }
        if (!id) {
          try {
            const stored = localStorage.getItem("activeJobId");
            if (stored) {
              id = stored;
              setJobId(stored);
            }
          } catch {}
        }
        const res = id ? await getJobWithRelations(id) : null;
        setData(res);
        setLinkedTasks(extractLinkedTasks(res));
        setRaciRows(extractRaciRows(res));
        setDisplayCurrency(res?.currencyType || "USD");
        if (id) {
          const [paei, capi] = await Promise.all([
            getPaeiByJobId(id),
            getCapiByJobId(id),
          ]);
          setPaeiValues(paei ? { ...PAEI_DEFAULTS, ...paei } : PAEI_DEFAULTS);
          setCapiValues(capi ? { ...CAPI_DEFAULTS, ...capi } : CAPI_DEFAULTS);
        }
      } catch (e) {
        toast.error(e?.message || "Failed to load job");
      } finally {
        setLoading(false);
      }
    })();
  }, [jobIdCtx, routeJobId, setJobId]);

  const goBack = () => {
    try {
      localStorage.removeItem("activeJobId");
    } catch {}
    let returnTo = null;
    try {
      returnTo = localStorage.getItem("jobReturnTo");
      localStorage.removeItem("jobReturnTo");
    } catch {}
    if (returnTo === "task-detail") {
      let targetTaskId = null;
      try {
        targetTaskId = localStorage.getItem("activeTaskId");
      } catch {}
      if (targetTaskId) {
        setActiveSection("task-detail", { taskId: targetTaskId });
        return;
      }
      setActiveSection("task-management");
      return;
    } else {
      setActiveSection("job-management");
    }
  };

  const companyName = useMemo(
    () => data?.company?.name || data?.company_name || "-",
    [data],
  );
  const functionName = useMemo(
    () => data?.function?.name || data?.function_name || "-",
    [data],
  );

  const jobStatusLabel = useMemo(() => {
    return (
      data?.job_status?.description ||
      data?.status?.description ||
      data?.jobStatus?.description ||
      (data?.status_id ? `Status ${data.status_id}` : "-")
    );
  }, [data]);

  const jobCategoryLabel = useMemo(() => {
    return (
      data?.job_category?.description ||
      data?.category?.description ||
      data?.jobCategory?.description ||
      (data?.category_id ? `Category ${data.category_id}` : "-")
    );
  }, [data]);

  const parentJobLabel = useMemo(() => {
    // API may return a nested parentJob object, or just parent_job_id
    const parent =
      data?.parentJob ||
      data?.parent_job ||
      data?.ParentJob ||
      null;
    if (parent) return parent.name || `Job #${parent.job_id ?? parent.id}`;
    if (data?.parent_job_id != null) return `Job #${data.parent_job_id}`;
    return "-";
  }, [data]);

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Job Details</h2>
            <p className="page-subtitle">
              {data?.name ?
                `Viewing: ${data.name}`
              : "Read-only view of a job and its relations"}
              {data?.jobCode || data?.job_code ?
                ` (${data.jobCode || data.job_code})`
              : ""}
            </p>
          </div>
          <div className="roles-toolbar">
            <button className="secondary-btn" onClick={goBack}>
              ← Back
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        {loading ?
          <div className="no-results">Loading...</div>
        : !data ?
          <div className="no-results">Job not found</div>
        : <SideTabs
            defaultActiveId={(() => {
              try {
                return localStorage.getItem("jobDetailsActiveTab") || "basic";
              } catch {
                return "basic";
              }
            })()}
            tabs={[
              {
                id: "basic",
                label: "Basic Details",
                content: (
                  <div className="role-card">
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Job Name</label>
                        <input value={data.name || ""} disabled />
                      </div>
                      <div className="form-group">
                        <label>Job Code</label>
                        <input
                          value={data.jobCode || data.job_code || ""}
                          disabled
                        />
                      </div>
                      <div className="form-group">
                        <label>Hourly Rate</label>
                        <input
                          value={
                            data.hourlyRate != null ?
                              formatMoney(
                                convertCurrency(
                                  data.hourlyRate,
                                  data.currencyType || "USD",
                                  displayCurrency,
                                  rates,
                                ),
                                displayCurrency,
                              )
                            : ""
                          }
                          disabled
                        />
                      </div>
                      <div className="form-group">
                        <label>Currency</label>
                        <CurrencySelector
                          value={displayCurrency}
                          onChange={setDisplayCurrency}
                          onRefresh={onRefreshRates}
                          refreshing={refreshingRates}
                        />
                      </div>
                      <div className="form-group">
                        <label>Max Hours Per Day</label>
                        <input
                          value={
                            data.maxHoursPerDay != null ?
                              String(data.maxHoursPerDay)
                            : ""
                          }
                          disabled
                        />
                      </div>
                      <div className="form-group">
                        <label>Staff count</label>
                        <input
                          value={
                            data.staff_count != null ?
                              String(data.staff_count)
                            : ""
                          }
                          disabled
                        />
                      </div>
                      <div className="form-group">
                        <label>Days Per Week</label>
                        <input
                          value={
                            data.days_per_week != null ?
                              String(data.days_per_week)
                            : data.daysPerWeek != null ?
                              String(data.daysPerWeek)
                            : ""
                          }
                          disabled
                        />
                      </div>
                      <div className="form-group">
                        <label>Capacity Buffer (%)</label>
                        <input
                          value={
                            data.capacity_buffer != null ?
                              String(data.capacity_buffer)
                            : data.capacityBuffer != null ?
                              String(data.capacityBuffer)
                            : ""
                          }
                          disabled
                        />
                      </div>
                      <div className="form-group">
                        <label>Function</label>
                        <input value={functionName} disabled />
                      </div>
                      <div className="form-group">
                        <label>Company</label>
                        <input value={companyName} disabled />
                      </div>
                      <div className="form-group">
                        <label>Parent Job</label>
                        <input value={parentJobLabel} disabled />
                      </div>
                      <div className="form-group">
                        <label>Job Status</label>
                        <input value={jobStatusLabel} disabled />
                      </div>
                      <div className="form-group">
                        <label>Job Category</label>
                        <input value={jobCategoryLabel} disabled />
                      </div>
                      <div className="form-group">
                        <label>Level Name</label>
                        <input
                          value={data.job_level?.level_name || "-"}
                          disabled
                        />
                      </div>
                      <div className="form-group">
                        <label>Level Rank</label>
                        <input
                          value={
                            data.job_level?.level_rank != null ?
                              String(data.job_level.level_rank)
                            : ""
                          }
                          disabled
                        />
                      </div>
                      <div className="form-group full">
                        <label>Level Description</label>
                        <input
                          value={data.job_level?.description || ""}
                          disabled
                        />
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                id: "description",
                label: "Description",
                content: (
                  <div className="role-card">
                    <RichTextEditor
                      label="Job Description"
                      value={data.description || ""}
                      readOnly
                      height={280}
                    />
                  </div>
                ),
              },
              {
                id: "skills",
                label: "Skills",
                content: (
                  <div className="role-card">
                    <div
                      className="section-title"
                      style={{ marginBottom: 12, fontWeight: 600 }}
                    >
                      Skills
                    </div>
                    <SkillTable
                      mode="view"
                      skills={(Array.isArray(data.jobSkills) ?
                        data.jobSkills
                      : []
                      ).map((js) => ({
                        name: js?.skill?.name || "-",
                        skillDescription: js?.skill?.description || "",
                        level: js?.skill_level?.level_name || "",
                      }))}
                      levels={LEVELS}
                    />
                  </div>
                ),
              },
              {
                id: "raci",
                label: "RACI",
                content: (
                  <RaciMatrix
                    mode="view"
                    entityType="task"
                    tasks={linkedTasks}
                    rows={raciRows}
                  />
                ),
              },
              {
                id: "paei",
                label: "PAEI",
                content: (
                  <DimensionAllocationPanel
                    title="PAEI Management Style"
                    dimensions={PAEI_DIMENSIONS}
                    values={paeiValues}
                    onChange={() => {}}
                    disabled
                  />
                ),
              },
              {
                id: "capi",
                label: "CAPI",
                content: (
                  <DimensionAllocationPanel
                    title="CAPI — Authority, Power & Influence"
                    dimensions={CAPI_DIMENSIONS}
                    values={capiValues}
                    onChange={() => {}}
                    disabled
                  />
                ),
              },
            ]}
          />
        }
      </div>
    </div>
  );
};

export default JobDetailPage;
